import React from 'react'
import {
  Menu,
  Tooltip,
  Dropdown,
  DatePicker
} from 'antd'
import {
  ExportOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  SearchOutlined,
  FilterOutlined
} from '@ant-design/icons'

import Button from '../../../components/common/button/button'
import dayjs from 'dayjs'

const { RangePicker } = DatePicker

/**
 * Format currency value to Philippine Peso
 * @param {number} value - The numeric value to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, decimals = 2) => {
  return `₱${Number(value).toFixed(decimals)}`
}

/**
 * Format date and time to: YYYY-MM-DD H:MM AM/PM
 * @param {Date|string} dateTime - The date/time to format
 * @returns {string} Formatted date and time string
 */
export const formatCreatedAtDateTime = (dateTime) => {
  if (!dateTime) return 'N/A'
  
  const date = new Date(dateTime)
  
  // Get date components
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  
  // Get time components
  let hours = date.getHours()
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  
  // Convert to 12-hour format
  hours = hours % 12
  hours = hours ? hours : 12 // 0 should be 12
  
  return `${year}-${month}-${day} ${hours}:${minutes} ${ampm}`
}

/**
 * Get unique values from an array of objects for a specific field
 * @param {Array} data - Array of objects
 * @param {string} field - Field to extract unique values from
 * @returns {Array} Array of unique values
 */
export const getUniqueValues = (data, field) => {
  return [...new Set(data.map(item => item[field]).filter(value => value && value.trim() !== ''))]
}

/**
 * Get column search props for text search
 * @param {string} dataIndex - Column data index
 * @param {string} title - Column title
 * @param {Component} Input - Input component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @returns {Object} Column search props object
 */
export const getColumnSearchProps = (dataIndex, title, Input, Button, SearchOutlined) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <Input
        placeholder={`Search ${title}`}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm({ closeDropdown: true })}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            if (clearFilters) {
              clearFilters()
            }

            setSelectedKeys([])

            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    // If no value or empty string, don't filter - show all records
    if (!value || value === '') {
      return true
    }

    // Handle cases where record[dataIndex] might be null or undefined
    if (record[dataIndex] === null || record[dataIndex] === undefined) {
      return false
    }

    // Convert both record value and search value to lowercase string for comparison
    return record[dataIndex]
      .toString()
      .toLowerCase()
      .includes(value.toLowerCase())
  }
})

/**
 * Get date filter props with correct date range filtering
 * @param {Component} RangePicker - DatePicker.RangePicker component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @returns {Object} Date filter props object
 */
export const getDateFilterProps = (RangePicker, Button, SearchOutlined) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    // Create a ref to track if this is a reset operation
    const isResetting = React.useRef(false)

    return (
      <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
        <RangePicker
          value={selectedKeys[0]}
          onChange={(dates) => {
            setSelectedKeys(dates ? [dates] : [])
          }}
          disabledDate={(current) => {
            return current && current > dayjs().endOf('month')  // Block only future months
          }}

          defaultPickerValue={[dayjs().subtract(1, 'month').startOf('month'), dayjs().endOf('day')]}

          onOpenChange={(open) => {
            if (open) {
              // Reset calendar view to current month when picker opens
              setSelectedKeys(selectedKeys[0] || [dayjs().startOf('month'), dayjs().endOf('month')])
            }
          }}
          dropdownClassName="hide-range-picker-navigation"
          style={{ marginBottom: 8, display: 'block', width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="primary"
            onClick={() => {
              confirm({ closeDropdown: true })
            }}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()

              // Mark as reset operation
              isResetting.current = true

              // Clear filters and reset
              if (clearFilters) {
                clearFilters()
              }

              setSelectedKeys([])

              // Important: Pass clearFilters flag to ensure table knows this is a reset
              confirm({
                closeDropdown: true,
                clearFilters: true
              })

              // Reset flag after operation
              setTimeout(() => {
                isResetting.current = false
              }, 100)
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </div>
      </div>
    )
  },
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    // If value is undefined or empty, it means filter has been reset
    if (!value || !value[0] || !value[1]) {
      return true  // Show all records
    }

    try {
      // Get start and end dates from the date picker
      const startDate = value[0].startOf('day')
      const endDate = value[1].endOf('day')

      // Parse the record's date with dayjs
      const recordDate = dayjs(record.dateObj || record.createdAt)

      // Check if the record's date is within the selected range (inclusive)
      return (recordDate.isAfter(startDate) || recordDate.isSame(startDate)) &&
        (recordDate.isBefore(endDate) || recordDate.isSame(endDate))
    } catch (error) {
      console.error('Error in date filter:', error)
      return true  // Show all records on error
    }
  }
})

/**
 * Get custom filter with search capability for dropdown filters
 * @param {string} dataIndex - Column data index
 * @param {Array} options - Array of filter options
 * @param {Component} Input - Input component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @param {Component} FilterOutlined - Filter icon component
 * @param {Component} Checkbox - Checkbox component
 * @returns {Object} Custom filter props object
 */

const CustomFilterDropdown = ({ dataIndex, options, selectedKeys, setSelectedKeys, confirm, clearFilters, Input, Button, SearchOutlined, Checkbox }) => {
  const [searchText, setSearchText] = React.useState('')
  const effectiveOptions = options.length > 0 ? options : ['Loading options...']
  const filteredOptions = effectiveOptions.filter(option => {
    if (!option) return false
    return option.toString().toLowerCase().includes(searchText.toLowerCase())
  })

  return (
    <div
      style={{
        padding: 16,
        background: '#fff',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: 240
      }}
      onClick={e => e.stopPropagation()}
    >
      <Input
        placeholder={`Search ${dataIndex}...`}
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        style={{ marginBottom: 8 }}
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
      />

      <div style={{ maxHeight: 200, overflow: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {filteredOptions.map((option, index) => (
            <div key={option || `empty-${index}`} style={{ margin: '8px 0' }}>
              <Checkbox
                checked={selectedKeys.includes(option)}
                onChange={e => {
                  const newSelectedKeys = e.target.checked
                    ? [...selectedKeys, option]
                    : selectedKeys.filter(key => key !== option)
                  setSelectedKeys(newSelectedKeys)
                }}
                style={{ width: '100%', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ marginLeft: 8 }}>{option || '(Empty)'}</span>
              </Checkbox>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: 16
      }}>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            setSearchText('')
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={(e) => {
            e.stopPropagation()
            confirm({ closeDropdown: true })
          }}
          size="small"
        >
          OK
        </Button>
      </div>
    </div>
  )
}

export const getCustomFilterWithSearch = (
  dataIndex,
  options,
  Input,
  Button,
  SearchOutlined,
  FilterOutlined,
  Checkbox
) => {
  return {
    filterDropdown: (props) => (
      <CustomFilterDropdown
        {...props}
        dataIndex={dataIndex}
        options={options}
        Input={Input}
        Button={Button}
        SearchOutlined={SearchOutlined}
        Checkbox={Checkbox}
      />
    ),
    filterIcon: filtered => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      if (!value) return true
      if (record[dataIndex] == null) return value === null || value === ''
      if (value === '----') return record[dataIndex] === '----' || !record[dataIndex]
      return record[dataIndex].toString() === value.toString()
    }
  }
}


/**
 * Creates table columns configuration with updated order and new columns
 * @param {function} showDetailsModal - Function to show record details modal
 * @param {function} handleExportPDF - Function to handle PDF export
 * @param {function} handleExportCSV - Function to handle CSV export
 * @param {object} tableFilters - Current table filters
 * @param {array} checkerNames - List of unique checker names for filter
 * @param {array} createdByNames - List of unique created by names for filter
 * @param {Component} Input - Input component from Ant Design
 * @param {Component} Checkbox - Checkbox component from Ant Design
 * @returns {Array} Column configuration for Ant Design Table
 */
export const getTableColumns = (
  showDetailsModal,
  handleExportPDF,
  handleExportCSV,
  tableFilters = {},
  checkerNames = [],
  createdByNames = [],
  Input,
  Checkbox
) => {
  return [
    // 1. Sr. No.
    {
      title: 'Sr. No.',
      dataIndex: 'serialNo',
      key: 'serialNo',
      width: 80,
      fixed: 'left',
      className: 'column-sr-no',
      align: 'center'
    },

    // 2. Denominations
    {
      title: 'Denominations',
      key: 'denomination',
      width: 180,
      render: (_, record) => {
        // Count the number of different denominations with values
        const denominationsWithValues = record.denomination.filter(d => d.count > 0).length

        return (
          <div className="denomination-cell">
            <div className="denomination-info">
              <span
                className="strong-text clickable-text"
                onClick={() => showDetailsModal(record)}
              >
                {denominationsWithValues} denominations
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation()
                  showDetailsModal(record)
                }}
                className="info-icon"
              >
                <InfoCircleOutlined style={{ color: '#ad0270' }} />
              </span>
            </div>
          </div>
        )
      }
    },

    // 3. Checker
    {
      title: 'Checker',
      dataIndex: 'checker',
      key: 'checker',
      width: 120,
      filteredValue: tableFilters.checker || null,
      ...getCustomFilterWithSearch('checker', checkerNames, Input, Button, SearchOutlined, FilterOutlined, Checkbox),
      render: text => <span>{text}</span>
    },

    // 4. Total
    {
      title: 'Total',
      dataIndex: 'totalDenomination',
      key: 'total',
      width: 120,
      className: 'column-total',
      align: 'right',
      render: (text) => <span className="strong-text">{formatCurrency(text)}</span>
    },

    // 5. Discrepancy
    {
      title: 'Discrepancy',
      dataIndex: 'discrepancy',
      key: 'discrepancy',
      width: 120,
      align: 'right',
      render: (text, record) => {
        // Only show discrepancy if it's negative (shortage)
        const calculatedDiscrepancy = record.totalDenomination - (window.pettySystemCashOnHand || 0)
        if (calculatedDiscrepancy < 0) {
          return <span className="negative-discrepancy">{formatCurrency(Math.abs(calculatedDiscrepancy))}</span>
        }
        return <span>---</span>
      }
    },

    // 6. Surplus
    {
      title: 'Surplus',
      dataIndex: 'surplus',
      key: 'surplus',
      width: 120,
      align: 'right',
      render: (text, record) => {
        const calculatedSurplus = record.totalDenomination - (window.pettySystemCashOnHand || 0)
        if (calculatedSurplus > 0) {
          return <span className="positive-surplus" style={{ color: '#52c41a' }}>{formatCurrency(calculatedSurplus)}</span>
        }
        return <span className="empty-value">---</span>
      }
    },

    // 7. Created By
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      filteredValue: tableFilters.createdBy || null,
      ...getCustomFilterWithSearch('createdBy', createdByNames, Input, Button, SearchOutlined, FilterOutlined, Checkbox),
      render: (createdBy) => <span>{createdBy || 'System'}</span>
    },

    // 8. Created At (replaces Date & Time)
    {
      title: 'Created At',
      dataIndex: 'date',
      key: 'date',
      width: 180,
      filteredValue: tableFilters.date || null,
      ...getDateFilterProps(RangePicker, Button, SearchOutlined),
      render: (text, record) => `${record.date} ${record.time}`
    },

    // 9. Actions
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="action-cell">
            <Dropdown
              overlay={
                <Menu>
                  <Menu.Item
                    key="1"
                    icon={<FilePdfOutlined />}
                    onClick={() => handleExportPDF(record)}
                  >
                    Export as PDF
                  </Menu.Item>
                  <Menu.Item
                    key="2"
                    icon={<FileExcelOutlined />}
                    onClick={() => handleExportCSV(record)}
                  >
                    Export as CSV
                  </Menu.Item>
                </Menu>
              }
              placement="bottomRight"
              trigger={['click']}
            >
              <Tooltip title="Export">
                <Button
                  type="text"
                  icon={<ExportOutlined />}
                  size="small"
                />
              </Tooltip>
            </Dropdown>
          </div>
        )
      }
    }
  ]
}

/**
 * Configure responsive properties for table based on screen size
 * @param {Array} columns - Table column configuration
 * @returns {object} Table properties
 */
export const getTableProps = (columns) => {
  const isMobile = window.innerWidth <= 992
  return {
    scroll: {
      y: 'calc(100vh - 550px)',
      x: 'max-content'
    },
    sticky: isMobile ? false : { offsetHeader: 0 },
    // Remove fixed properties for mobile view
    columns: isMobile ? columns.map(col => ({ ...col, fixed: undefined })) : columns
  }
}

/**
 * Handle PDF export functionality with proper time formatting
 * @param {object} record - Cash count record to export
 */
export const handleExportPDF = (record) => {
  // Get the current store name from window object
  const currentStoreName = window.currentStoreName || 'Store'

  // Get the system cash on hand value
  const systemCashOnHand = window.pettySystemCashOnHand || 0

  // Calculate the discrepancy
  const discrepancyAmount = record.totalDenomination - systemCashOnHand
  const isSurplus = discrepancyAmount > 0
  const isShortage = discrepancyAmount < 0

  // Format the created date and time properly
  const formatDateTime = (record) => {
    if (record.createdAt) {
      // If createdAt exists, use it (it's already formatted in the correct format)
      return record.createdAt
    } else if (record.createdAtRaw) {
      // Format from raw date using the utility function
      return formatCreatedAtDateTime(record.createdAtRaw)
    }
    return 'N/A'
  }

  const formattedDateTime = formatDateTime(record)

  // Create discrepancy/surplus rows based on condition
  let discrepancyRow = ''
  let surplusRow = ''

  if (isShortage) {
    discrepancyRow = `
      <tr>
        <td>Discrepancy</td>
        <td class="amount negative-discrepancy">${Math.abs(discrepancyAmount).toFixed(2)}</td>
      </tr>
    `
  } else {
    discrepancyRow = `
      <tr>
        <td>Discrepancy</td>
        <td class="amount">0.00</td>
      </tr>
    `
  }

  if (isSurplus) {
    surplusRow = `
      <tr>
        <td>Surplus</td>
        <td class="amount positive-surplus">${discrepancyAmount.toFixed(2)}</td>
      </tr>
    `
  }

  // Using Modal component to show a printable view
  const printableContent = `
    <html>
      <head>
        <title>${currentStoreName} - Cash Count Record #${record.recordNumber || record.countId}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h2 { text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; }
          th { background-color: #f2f2f2; }
          .highlight { background-color: #fffbe6; }
          .total { font-weight: bold; }
          .negative-discrepancy { color: #ff4d4f; }
          .positive-surplus { color: #52c41a; }
          .footer { font-weight: bold; }
          .note { font-style: italic; margin-top: 20px; }
          @media print {
            button { display: none; }
          }
          .amount { text-align: right; }
        </style>
      </head>
      <body>
        <h2>${currentStoreName} - Petty Cash - Cash Count</h2>
        <p style="text-align: right;">Record #${record.recordNumber || record.countId}</p>
        
        <table>
          <tr>
            <th>DENOMINATION</th>
            <th>COUNT</th>
            <th>TOTAL AMT</th>
          </tr>
          ${record.denomination.map(item => `
            <tr class="${item.count > 0 ? 'highlight' : ''}">
              <td style="text-align: center;">${item.value}</td>
              <td style="text-align: center;">${item.count}</td>
              <td style="text-align: right;">${item.total.toFixed(2)}</td>
            </tr>
          `).join('')}
          <tr>
            <td class="total">TOTAL COH</td>
            <td></td>
            <td class="total amount">${record.totalDenomination.toFixed(2)}</td>
          </tr>
        </table>
        
        <table style="width: 50%;">
          <tr>
            <td>Ideal Petty Cash Amt</td>
            <td class="amount">${systemCashOnHand.toFixed(2)}</td>
          </tr>
          ${discrepancyRow}
          ${surplusRow}
          <tr class="highlight">
            <td>Checker:</td>
            <td class="amount">${record.checker}</td>
          </tr>
          <tr class="highlight">
            <td>Created By:</td>
            <td class="amount">${record.createdBy || 'System'}</td>
          </tr>
          <tr class="highlight">
            <td>Created At:</td>
            <td class="amount">${formattedDateTime}</td>
          </tr>
        </table>
        
        <p class="note">Note: All amounts are in Philippine Pesos (PHP)</p>
        
        <div class="footer" style="margin-top: 20px;">
          <button onclick="window.print();" style="padding: 10px 20px; background-color: #1890ff; color: white; border: none; border-radius: 4px; cursor: pointer;">Print PDF</button>
        </div>
      </body>
    </html>
  `

  // Create a popup window with the content
  const printWindow = window.open('', '_blank', 'width=800,height=600')
  printWindow.document.write(printableContent)
  printWindow.document.close()
}

/**
 * Handle CSV export functionality with proper time formatting
 * @param {object} record - Cash count record to export
 */
export const handleExportCSV = (record) => {
  // Get the current store name from window object
  const currentStoreName = window.currentStoreName || 'Store'

  // Get the system cash on hand value
  const systemCashOnHand = window.pettySystemCashOnHand || 0

  // Calculate the discrepancy
  const discrepancyAmount = record.totalDenomination - systemCashOnHand
  const isSurplus = discrepancyAmount > 0
  const isShortage = discrepancyAmount < 0

  // Format the created date and time properly
  const formatDateTime = (record) => {
    if (record.createdAt) {
      // If createdAt exists, use it (it's already formatted in the correct format)
      return record.createdAt
    } else if (record.createdAtRaw) {
      // Format from raw date using the utility function
      return formatCreatedAtDateTime(record.createdAtRaw)
    }
    return 'N/A'
  }

  const formattedDateTime = formatDateTime(record)

  // Define the CSV rows for discrepancy and surplus
  const discrepancyValue = isShortage ? Math.abs(discrepancyAmount).toFixed(2) : '0.00'
  const surplusRow = isSurplus ? `"Surplus","","${discrepancyAmount.toFixed(2)}"` : ''

  // Create CSV content
  const csvContent = [
    `"${currentStoreName} - Cash Count Record #${record.recordNumber || record.countId}"`,
    '"Checker","' + record.checker + '"',
    '"Created By","' + (record.createdBy || 'System') + '"',
    '"Created At","' + formattedDateTime + '"',
    '',
    '"DENOMINATION","COUNT","TOTAL AMT"',
    ...record.denomination.map(item =>
      '"' + item.value + '","' + item.count + '","' + item.total.toFixed(2) + '"'
    ),
    '',
    '"TOTAL COH","","' + record.totalDenomination.toFixed(2) + '"',
    '"Ideal Petty Cash Amt","","' + systemCashOnHand.toFixed(2) + '"',
    '"Discrepancy","","' + discrepancyValue + '"',
    surplusRow,
    '',
    '"Note: All amounts are in Philippine Pesos (PHP)"'
  ].filter(Boolean).join('\n')

  // Create a blob and downloadable link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  // Create a temporary link and trigger download
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${currentStoreName}_CashCount_${record.recordNumber || record.countId}.csv`)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
